import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Sparkles, ArrowRight } from "lucide-react";
import { aiApi, runsApi } from "../api/endpoints";
import { AIMandatedLabel } from "../components/ai/AIMandatedLabel";
import { FeasibilityBadge } from "../components/chips/FeasibilityBadge";
import { SkeletonTable } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { cn } from "../lib/utils";

/**
 * Phase 7c — Recovery Strategies board.
 *
 * Workspace-wide aggregate view over the most recent completed run:
 * per-process status, confidence, gap-dimension count, open-finding
 * count, and whether cached AI strategy analyses exist. NO synthetic
 * resilience score. NO composite metric hiding INSUFFICIENT_DATA.
 *
 * All values are deterministic (rendered engine output). AI analyses
 * appear only as a per-row indicator; drilling into ProcessDetail
 * preserves the trust contract.
 */

const CRITICALITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const STATUS_SORT_WEIGHT = {
  NOT_FEASIBLE: 0,
  AT_RISK: 1,
  FEASIBLE: 2,
  INSUFFICIENT_DATA: 3,
};

export default function StrategyInboxPage() {
  const { workspaceId } = useParams();
  const [runs, setRuns] = useState(null);
  const [processes, setProcesses] = useState(null);
  const [findings, setFindings] = useState(null);
  const [strategyAnalyses, setStrategyAnalyses] = useState([]);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [confidenceFilter, setConfidenceFilter] = useState("");
  const [criticalityFilter, setCriticalityFilter] = useState("");
  const [hasStrategyFilter, setHasStrategyFilter] = useState(""); // "", "yes", "no"
  const [hasOpenFindingsFilter, setHasOpenFindingsFilter] = useState("");
  const [sortBy, setSortBy] = useState("severity"); // severity | name | status
  const [selectedRunId, setSelectedRunId] = useState(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    runsApi.list(workspaceId).then(async (list) => {
      if (!alive) return;
      setRuns(list);
      const latestCompleted = list.find((r) => r.status === "completed");
      if (!latestCompleted) return;
      setSelectedRunId(latestCompleted.id);
    }).catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!selectedRunId) return;
    let alive = true;
    setProcesses(null);
    setFindings(null);
    Promise.all([
      runsApi.processes(workspaceId, selectedRunId),
      runsApi.findings(workspaceId, selectedRunId, { page_size: 500 }),
      aiApi.listStrategyAnalyses(workspaceId, { limit: 200 }).catch(() => ({ items: [] })),
    ])
      .then(([p, f, s]) => {
        if (!alive) return;
        setProcesses(p);
        setFindings(f);
        setStrategyAnalyses(s.items || []);
      })
      .catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
  }, [workspaceId, selectedRunId]);

  const strategyIndex = useMemo(() => {
    const idx = new Map();
    for (const s of strategyAnalyses || []) {
      if (!s.process_id) continue;
      const cur = idx.get(s.process_id) || 0;
      idx.set(s.process_id, cur + 1);
    }
    return idx;
  }, [strategyAnalyses]);

  const openFindingsIndex = useMemo(() => {
    const idx = new Map();
    for (const f of (findings?.items || [])) {
      if (!f.process_id) continue;
      const cur = idx.get(f.process_id) || 0;
      idx.set(f.process_id, cur + 1);
    }
    return idx;
  }, [findings]);

  const rows = useMemo(() => {
    if (!processes) return null;
    return processes.map((p) => {
      const dims = p.per_dimension || {};
      const gapDimensions = Object.entries(dims).filter(
        ([, d]) => d?.status === "GAP",
      ).length;
      const unknownDimensions = Object.entries(dims).filter(
        ([, d]) => d?.status === "UNKNOWN",
      ).length;
      const openFindings = openFindingsIndex.get(p.process_id) || 0;
      const strategyCount = strategyIndex.get(p.process_id) || 0;
      const criticality = (p.criticality || p.process_criticality || "").toUpperCase();
      return {
        ...p,
        gap_dimensions: gapDimensions,
        unknown_dimensions: unknownDimensions,
        open_findings: openFindings,
        strategy_count: strategyCount,
        criticality,
      };
    });
  }, [processes, openFindingsIndex, strategyIndex]);

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (statusFilter) out = out.filter((r) => r.overall_status === statusFilter);
    if (confidenceFilter) out = out.filter((r) => r.feasibility_confidence === confidenceFilter);
    if (criticalityFilter) out = out.filter((r) => r.criticality === criticalityFilter);
    if (hasStrategyFilter === "yes") out = out.filter((r) => r.strategy_count > 0);
    if (hasStrategyFilter === "no") out = out.filter((r) => r.strategy_count === 0);
    if (hasOpenFindingsFilter === "yes") out = out.filter((r) => r.open_findings > 0);
    if (hasOpenFindingsFilter === "no") out = out.filter((r) => r.open_findings === 0);
    out = [...out];
    if (sortBy === "severity") {
      out.sort((a, b) => {
        const s = (STATUS_SORT_WEIGHT[a.overall_status] ?? 99)
          - (STATUS_SORT_WEIGHT[b.overall_status] ?? 99);
        if (s !== 0) return s;
        const g = b.gap_dimensions - a.gap_dimensions;
        if (g !== 0) return g;
        return b.open_findings - a.open_findings;
      });
    } else if (sortBy === "name") {
      out.sort((a, b) => (a.process_name || "").localeCompare(b.process_name || ""));
    } else if (sortBy === "status") {
      out.sort((a, b) => (a.overall_status || "").localeCompare(b.overall_status || ""));
    }
    return out;
  }, [rows, statusFilter, confidenceFilter, criticalityFilter,
      hasStrategyFilter, hasOpenFindingsFilter, sortBy]);

  if (error) return <ErrorState error={error} />;
  if (runs === null) return <SkeletonTable rows={5} cols={6} />;

  const completedRuns = runs.filter((r) => r.status === "completed");
  if (completedRuns.length === 0) {
    return (
      <div className="space-y-5" data-testid="strategy-inbox-page">
        <HeaderCopy />
        <EmptyState
          title="No completed analysis yet"
          description="Run an analysis to see recovery strategies for your processes."
          action={
            <Link
              to={`/w/${workspaceId}/import`}
              className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              data-testid="strategy-inbox-new-analysis"
            >
              New analysis
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="strategy-inbox-page">
      <HeaderCopy />

      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label htmlFor="strategies-run-select" className="text-[11px] uppercase tracking-wider text-slate-500">
              Run
            </label>
            <select
              id="strategies-run-select"
              data-testid="strategies-run-select"
              value={selectedRunId || ""}
              onChange={(e) => setSelectedRunId(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm min-w-[220px]"
            >
              {completedRuns.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <FilterSelect
            label="Status"
            testId="strategies-filter-status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              ["", "All"],
              ["FEASIBLE", "FEASIBLE"],
              ["AT_RISK", "AT_RISK"],
              ["NOT_FEASIBLE", "NOT_FEASIBLE"],
              ["INSUFFICIENT_DATA", "INSUFFICIENT_DATA"],
            ]}
          />
          <FilterSelect
            label="Confidence"
            testId="strategies-filter-confidence"
            value={confidenceFilter}
            onChange={setConfidenceFilter}
            options={[
              ["", "All"],
              ["HIGH", "HIGH"],
              ["MEDIUM", "MEDIUM"],
              ["LOW", "LOW"],
              ["CANNOT_DETERMINE", "CANNOT_DETERMINE"],
            ]}
          />
          <FilterSelect
            label="Criticality"
            testId="strategies-filter-criticality"
            value={criticalityFilter}
            onChange={setCriticalityFilter}
            options={[
              ["", "All"],
              ["CRITICAL", "CRITICAL"],
              ["HIGH", "HIGH"],
              ["MEDIUM", "MEDIUM"],
              ["LOW", "LOW"],
            ]}
          />
          <FilterSelect
            label="Strategy analysis"
            testId="strategies-filter-has-strategy"
            value={hasStrategyFilter}
            onChange={setHasStrategyFilter}
            options={[["", "All"], ["yes", "Yes"], ["no", "No"]]}
          />
          <FilterSelect
            label="Open findings"
            testId="strategies-filter-has-open"
            value={hasOpenFindingsFilter}
            onChange={setHasOpenFindingsFilter}
            options={[["", "All"], ["yes", "Yes"], ["no", "No"]]}
          />
          <FilterSelect
            label="Sort by"
            testId="strategies-sort"
            value={sortBy}
            onChange={setSortBy}
            options={[
              ["severity", "Severity (worst first)"],
              ["name", "Process name"],
              ["status", "Status"],
            ]}
          />
        </div>
      </div>

      {filteredRows === null ? (
        <SkeletonTable rows={6} cols={6} />
      ) : filteredRows.length === 0 ? (
        <EmptyState
          title="No matching processes"
          description="Adjust filters to see more."
        />
      ) : (
        <div className="rounded border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="strategies-board-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Process</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Confidence</th>
                  <th className="px-4 py-2.5">Gap dimensions</th>
                  <th className="px-4 py-2.5">Open findings</th>
                  <th className="px-4 py-2.5">AI strategy analyses</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.map((r) => (
                  <tr key={r.process_id} data-testid={`strategy-row-${r.process_id}`}>
                    <td className="px-4 py-3">
                      <Link
                        to={`/w/${workspaceId}/analyses/${selectedRunId}/processes/${r.process_id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {r.process_name || r.process_id}
                      </Link>
                      <div className="mt-0.5 text-[11px] font-mono text-slate-500">
                        {r.process_id}
                        {r.criticality && (
                          <span className="ml-2 uppercase tracking-wider">
                            · {r.criticality}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <FeasibilityBadge status={r.overall_status} size="md" />
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {r.feasibility_confidence || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <GapPill count={r.gap_dimensions} unknown={r.unknown_dimensions} />
                    </td>
                    <td className="px-4 py-3 text-slate-700 tabular-nums">
                      {r.open_findings}
                    </td>
                    <td className="px-4 py-3">
                      {r.strategy_count > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-violet-50 border border-violet-200 px-2 py-0.5 text-[11px] font-medium text-violet-800"
                          data-testid={`strategy-row-has-analysis-${r.process_id}`}
                        >
                          <Sparkles size={11} />
                          {r.strategy_count} cached
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/w/${workspaceId}/analyses/${selectedRunId}/processes/${r.process_id}/strategies`}
                        data-testid={`strategy-row-open-${r.process_id}`}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-100"
                      >
                        Analyse <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderCopy() {
  return (
    <header className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Workspace
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900" data-testid="strategies-title">
          Recovery Strategies
        </h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl leading-relaxed">
          Every process from the most recent completed analysis. Feasibility
          status and gap counts are deterministic. Cached AI strategy
          analyses are surfaced as a per-process indicator — no synthetic
          resilience score, no composite hiding INSUFFICIENT_DATA.
        </p>
      </div>
      <AIMandatedLabel testId="strategy-inbox-mandated-label" />
    </header>
  );
}

function FilterSelect({ label, testId, value, onChange, options }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={testId} className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <select
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </div>
  );
}

function GapPill({ count, unknown }) {
  if (!count && !unknown) {
    return <span className="text-[11px] text-slate-400">None</span>;
  }
  return (
    <div className="flex items-center gap-2">
      {count > 0 && (
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium",
            count >= 3
              ? "bg-red-50 text-red-800 border border-red-200"
              : "bg-amber-50 text-amber-800 border border-amber-200",
          )}
        >
          <AlertTriangle size={11} />
          {count} GAP
        </span>
      )}
      {unknown > 0 && (
        <span className="inline-flex items-center gap-1 rounded bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 text-[11px] font-medium">
          {unknown} UNKNOWN
        </span>
      )}
    </div>
  );
}
